import type { ColumnsBlockProps } from "@/puck/types";

type SlotBlock = {
  type: string;
  props: Record<string, unknown> & { id: string };
};

function chatAskAnythingMockup(id: string): SlotBlock {
  return {
    type: "ChatMockup",
    props: {
      id,
      header: "GailGPT",
      messages: [
        {
          role: "user",
          text: "How many auto policies renew next month?",
          tag: "",
          citationMarkers: [],
        },
        {
          role: "assistant",
          text: "342 policies, $1.2M in premium. 18 are flagged price-sensitive based on Q3 quote shopping.",
          tag: "policies.db",
          citationMarkers: [],
        },
        {
          role: "user",
          text: "Break it down by underwriter.",
          tag: "",
          citationMarkers: [],
        },
      ],
      citations: [],
    },
  };
}

function chatCitationsMockup(id: string): SlotBlock {
  return {
    type: "ChatMockup",
    props: {
      id,
      header: "GailGPT · cited",
      messages: [
        {
          role: "user",
          text: "Can we charge a returned-payment fee in California?",
          tag: "",
          citationMarkers: [],
        },
        {
          role: "assistant",
          text: "Yes — capped at $25 for the first return, $35 for subsequent within 12 months. Your servicing policy adds a 7-day cure window before assessing.",
          tag: "",
          citationMarkers: [1, 2],
        },
      ],
      citations: [
        {
          index: 1,
          label: "Cal. Civ. Code §1719",
          type: "state law",
        },
        {
          index: 2,
          label: "Servicing SOP §4.2",
          type: "internal",
        },
      ],
    },
  };
}

function chartTrendsMockup(id: string): SlotBlock {
  return {
    type: "ChartMockup",
    props: {
      id,
      rows: [
        { label: "Pricing", value: 412 },
        { label: "Coverage", value: 287 },
        { label: "Payment", value: 154 },
        { label: "Claims status", value: 98 },
      ],
      footnote:
        "Pricing inquiries up 23% QoQ — most common topic across your book.",
      showTrendArrow: true,
    },
  };
}

function featureCard(
  id: string,
  title: string,
  description: string,
  visual: SlotBlock,
): SlotBlock {
  return {
    type: "Card",
    props: {
      id,
      title,
      description,
      visual: [visual],
    },
  };
}

/** Composed busywork section matching the GAIL-228 / redesign design. */
export function buildBusyworkSectionProps(
  id = "busywork-section",
): ColumnsBlockProps & { id: string } {
  const askCard = featureCard(
    "busywork-card-ask",
    "Ask anything",
    "Query your book in plain language — policies, claims, payments.",
    chatAskAnythingMockup("busywork-chat-ask"),
  );
  const citationsCard = featureCard(
    "busywork-card-citations",
    "Cited to the regs",
    "Answers grounded in specific regulations, your policies, and your written procedures.",
    chatCitationsMockup("busywork-chat-citations"),
  );
  const trendsCard = featureCard(
    "busywork-card-trends",
    "Trends across your book",
    "What customers are calling about, asking about, complaining about — week over week.",
    chartTrendsMockup("busywork-chart-trends"),
  );

  return {
    id,
    columnCount: 3,
    showBorder: "none",
    eyebrow: "DOES THE WORK, NOT JUST THE TALKING",
    title: "The busywork your team dreads, <em>just done.</em>",
    subtitle:
      "Access Gail's conversational AI agents and the everyday insurance work runs itself, start to finish.",
    headerAlign: "center",
    column1: [askCard] as ColumnsBlockProps["column1"],
    column2: [citationsCard] as ColumnsBlockProps["column2"],
    column3: [trendsCard] as ColumnsBlockProps["column3"],
    column4: [],
    column5: [],
    column6: [],
  };
}
